import { IMAGES } from "./site";

export const STEEL_SECTORS = [
  {
    slug: "oil-gas-line-pipe",
    code: "API 5L",
    n: "01",
    name: "Steel for Oil & Gas",
    shortName: "Oil & Gas",
    sub: "Line pipe, process & structures",
    tagline: "Line pipe and process steels for onshore and offshore energy infrastructure.",
    image: IMAGES.steelCoils,
    description:
      "Line pipe and process steels form the backbone of energy infrastructure. We trade API 5L line pipe from Grade B through X80 for onshore and offshore pipelines and flowlines, carbon and alloy pipe steels for high-temperature process service, and stainless or duplex grades for corrosive and sour environments. All material ships with mill test certificates, with third-party inspection available on request.",
    points: [
      "API 5L Grade B, X42 – X80 — onshore & offshore pipelines and flowlines",
      "Carbon & alloy pipe steels — ASTM A106, A53; P5, P9, P11 for high-temperature service",
      "Stainless & duplex — 304, 316L, 2205 for corrosive and sour service",
    ],
    applications: ["Cross-country pipelines", "Flowlines & gathering lines", "Refinery process piping", "Offshore structures", "Compressor & pump stations"],
    gradeTable: {
      columns: ["Specification", "Grades", "Service"],
      rows: [
        ["API 5L", "B, X42, X52, X60, X65, X70, X80", "Onshore & offshore pipelines, flowlines"],
        ["ASTM A106 / A53", "Grade A, B", "Carbon steel pipe — general & pressure service"],
        ["ASTM A335 (alloy)", "P5, P9, P11", "High-temperature process service"],
        ["Stainless & duplex", "304, 316L, 2205", "Corrosive and sour service"],
      ],
    },
    gradeNotes: [
      { grade: "Full API 5L range", note: "PSL1 and PSL2, seamless or welded (ERW / LSAW / SSAW) per project requirement." },
      { grade: "Coating options", note: "Bare, 3LPE, FBE or concrete weight coating arranged ex-mill or ex-coater." },
      { grade: "Documentation", note: "Mill test certificates to EN 10204 3.1; third-party inspection (SGS / BV / TÜV) on request." },
    ],
    specsNote: "Typical trading ranges. Final contractual specifications are confirmed against the mill test certificate for each lot.",
    packaging: "Beveled or plain ends, 6 m / 12 m / double-random lengths, bundled or loose — bulk vessel or container.",
  },
  {
    slug: "water-transportation",
    code: "H2O",
    n: "02",
    name: "Steel for Water Transportation",
    shortName: "Water Transportation",
    sub: "Water pipelines & distribution",
    tagline: "Welded steel pipe for potable water transmission, distribution and slurry lines.",
    image: IMAGES.steelBeams,
    description:
      "Water infrastructure demands pipe that arrives on spec and on schedule. We trade API 5L and structural carbon steel pipe for potable water transmission, municipal distribution networks, irrigation and slurry service — with lining and coating options to match the medium and soil conditions.",
    points: [
      "API 5L Grade B, L245, L290/X42, L360/X52 — water and slurry transportation lines",
      "Structural & carbon steels — ST37, L235, L275, Fe 410, Fe 450 for welded water pipes and mains",
    ],
    applications: ["Potable water transmission", "Municipal distribution networks", "Irrigation systems", "Sewage & slurry lines", "Desalination plants"],
    gradeTable: {
      columns: ["Specification", "Grades", "Service"],
      rows: [
        ["API 5L", "B, L245, L290/X42, L360/X52", "Water and slurry transportation lines"],
        ["Structural carbon steels", "ST37, L235, L275", "Welded steel water pipes"],
        ["Indian grades", "Fe 410, Fe 450", "Water mains and distribution"],
      ],
    },
    gradeNotes: [
      { grade: "Lining options", note: "Cement mortar lining or liquid epoxy for potable water service." },
      { grade: "External coating", note: "3LPE, coal-tar epoxy or bituminous coating matched to soil conditions." },
      { grade: "Standards alignment", note: "AWWA C200 and equivalent national standards accommodated on request." },
    ],
    specsNote: "Typical trading ranges. Final contractual specifications are confirmed against the mill test certificate for each lot.",
    packaging: "Plain or beveled ends, 6 m / 12 m lengths, bundled or loose — bulk vessel, breakbulk or container.",
  },
  {
    slug: "construction-steel",
    code: "STR",
    n: "03",
    name: "Steel for Construction",
    shortName: "Construction",
    sub: "Buildings, infrastructure, industrial structures",
    tagline: "Structural sections and TMT reinforcement bars for buildings and infrastructure.",
    image: IMAGES.steelRebar,
    description:
      "From high-rise frames to industrial sheds and bridges, we trade structural sections to EN, ASTM and Indian standards alongside TMT reinforcement bars in all standard grades and diameters. Material is sourced from qualified mills with full traceability and shipped against project schedules.",
    points: [
      "Structural steels (EN) — S235, S275, S355, S420 for beams, columns and general structures",
      "Structural steels (ASTM) — A36, A572-50, A992 for high-strength heavy structures",
      "Indian standards — IS 2062, Fe 250, Fe 410 for general construction and fabrication",
      "TMT bars — Fe 415, Fe 500, Fe 550 in 8 mm to 32 mm diameters for reinforced concrete",
    ],
    applications: ["High-rise buildings", "Industrial sheds & PEBs", "Bridges & flyovers", "Power & process plants", "General fabrication"],
    gradeTable: {
      columns: ["Standard", "Grades", "Use"],
      rows: [
        ["EN 10025", "S235, S275, S355, S420", "Beams, columns, general structures"],
        ["ASTM", "A36, A572-50, A992", "High-strength beams, columns, heavy structures"],
        ["IS 2062", "Grade B, Fe 250, Fe 410", "General construction and fabrication"],
        ["TMT bars", "Fe 415, Fe 500, Fe 550 — 8 to 32 mm", "Reinforced concrete structures"],
      ],
    },
    gradeNotes: [
      { grade: "Sections & formats", note: "I/H beams, channels, angles, flats, plates and hollow sections per project BOM." },
      { grade: "Cut & bend", note: "Cut-to-length and cut-and-bend TMT services available ex-stockyard." },
      { grade: "Traceability", note: "Heat-number traceability with mill test certificates on every bundle." },
    ],
    specsNote: "Typical trading ranges. Final contractual specifications are confirmed against the mill test certificate for each lot.",
    packaging: "Bundles with steel strapping, tagged with heat numbers — breakbulk, bulk vessel or container.",
  },
  {
    slug: "piling-pipes",
    code: "PIL",
    n: "04",
    name: "Steel Piling Pipes",
    shortName: "Piling Pipes",
    sub: "Steel pipes for foundations",
    tagline: "Seamless and ERW steel pipes engineered for deep foundations and heavy loads.",
    image: IMAGES.steelPlant,
    description:
      "Steel piling pipes carry the loads of bridges, marine structures and high-rise foundations. We trade seamless and ERW piling pipe in diameters from 150 mm to 3,000 mm, with wall thickness and lengths engineered to project load and depth requirements — designed to withstand high loads, corrosive environments and deep ground conditions.",
    points: [
      "Seamless and ERW steel pipes for high load-bearing, durable piling",
      "Grades — API 5L (B, X42, X52), ASTM A106 / A53, EN 10255 / EN 10297",
      "Diameters 150 – 3,000 mm, wall thickness 6 – 20 mm+, lengths 6 m / 12 m / custom",
    ],
    applications: ["Bridge foundations", "Marine & jetty piling", "High-rise foundations", "Retaining structures", "Industrial plant foundations"],
    gradeTable: {
      columns: ["Parameter", "Specification"],
      rows: [
        ["Pipe type", "Seamless / ERW (Electric Resistance Welded)"],
        ["Grades", "API 5L B, X42, X52 · ASTM A106 / A53 · EN 10255 / EN 10297"],
        ["Diameter range", "150 – 3,000 mm (6\" – 120\")"],
        ["Wall thickness", "6 – 20 mm+, per load and depth requirements"],
        ["Lengths", "6 m / 12 m / customized to project"],
      ],
    },
    gradeNotes: [
      { grade: "Load-engineered", note: "Wall thickness and steel grade selected against axial load and driving conditions." },
      { grade: "Splicing", note: "Weld-on splice rings, drive shoes and conical points available with the pipe." },
      { grade: "Corrosion protection", note: "External coating or cathodic-protection-friendly bare finish per site conditions." },
    ],
    specsNote: "Typical trading ranges. Final contractual specifications are confirmed against the mill test certificate for each lot.",
    packaging: "Loose or bundled, beveled ends — breakbulk or bulk vessel to project port.",
  },
];

export const getSteelSector = (slug) => STEEL_SECTORS.find((s) => s.slug === slug);
